import type { OverviewShipment, RouteDetail, RouteSummary } from '../domain/routes';
import type { OrderPackage, PackageInfo } from '../domain/types';
import { mapOverview, mapRouteDetail } from './mapRoutes';
import { ApiError, type OverviewResponseDto, type RouteDetailResponseDto } from './types';

/**
 * Mock režim (`?mock=true`) – nahrazuje původní `debug.js`.
 * Slouží k vývoji na PC bez kamery a k proklikání všech stavů bez backendu.
 *
 * Kód řídí, co se vrátí:
 *   TEST-GO / TEST-SCHVALENI / TEST-PRIPRAVA / TEST-VRACENI
 *   TEST-STOP / TEST-ZRUSENA / TEST-VRACENO
 *   TEST-NEZNAMY  → neznámý stav (musí padnout na vrstvu EDGE)
 *   TEST-404      → nenalezená zásilka
 *   přípona -SK / -CZ přidá převoz, -PLNA přidá kompletní data
 *   -HOTOVO   → vícekusová, vše naskenováno (zelený pás kompletace)
 *   -CHYBI2   → vícekusová, chybí dva balíky (výpis „1, 3")
 *   -MIX1     → JEDNOKUSOVÁ objednávka (složení se nesmí vypsat – spec 5a;
 *               „jeden kus, ale smíšené složení“ už z pole balíků nejde složit)
 */
const BY_CODE: Record<string, string> = {
  'TEST-GO': 'Přeprava',
  'TEST-SCHVALENI': 'Schválení Přepravy',
  'TEST-PRIPRAVA': 'Příprava Zásilky',
  'TEST-VRACENI': 'Vrácení Odesilateli',
  'TEST-STOP': 'Dokončená',
  'TEST-ZRUSENA': 'Zrušená',
  'TEST-VRACENO': 'Vráceno Odesilateli',
};

export async function mockPackage(code: string): Promise<PackageInfo> {
  await new Promise((r) => setTimeout(r, 250));

  const upper = code.toUpperCase();
  if (upper.startsWith('TEST-404')) throw new ApiError('NOT_FOUND');

  const base = upper.replace(/-(SK|CZ|PLNA|HOTOVO|CHYBI2|MIX1)/g, '');
  const status = BY_CODE[base] ?? (base === 'TEST-NEZNAMY' ? 'Nějaký nový stav' : 'Přeprava');
  const full = upper.includes('PLNA') || upper.includes('HOTOVO') || upper.includes('CHYBI2');
  const singleMixed = upper.includes('MIX1');
  const sk = upper.includes('-SK');
  const cz = upper.includes('-CZ');

  return {
    scannedCode: code,
    shipmentId: 'c9e3ccbc-0325-484b-3e71-08dee81e97e7',
    shipmentNumber: full ? 'CB 2608 1300 4612' : null,
    statusCode: null,
    statusLabelRaw: status,
    sequence: 46,
    scanCount: full ? 2 : null,
    deliverAt: '2026-08-13T00:00:00',
    expeditionAt: full ? '2026-08-13T00:00:00' : null,
    transfer: sk || cz
      ? { direction: sk ? 'SK' : 'CZ', warehouseCode: sk ? 'SK' : 'CZ', confirmed: false }
      : null,
    pick: {
      driver: 'J. Kalian',
      routeNumber: full ? '118' : null,
      vehicleName: 'L12 · Renault Master',
      plate: '1ARI610',
    },
    recipient: {
      name: 'Lucie Dudáková',
      note: full ? 'spolupráce' : null,
      street: 'Italská 2315',
      city: full ? 'Kladno' : null,
      zip: full ? '27201' : null,
      lat: null,
      lon: null,
    },
    zone: 'CZ Denně – 2. sled',
    weightKg: 6,
    deliveryTime: '08:00–12:00',
    order: {
      packageIndex: singleMixed ? 1 : full ? 2 : null,
      packages: mockPackages(upper, full, singleMixed),
    },
    temperature: singleMixed ? 'CHILLED' : full ? 'FROZEN' : null,
    flags: { isRetryAttempt: full, isSecondPart: false },
  };
}

/**
 * Balíky objednávky. Vícekusové případy mají složení 1× chlazené + 2× mražené,
 * liší se jen tím, co je naskenované.
 *
 * Balík, který skladník drží v ruce (`packageIndex`), je vždy naskenovaný –
 * u `-CHYBI2` proto chybí 1 a 3, ne 2.
 */
function mockPackages(
  upper: string,
  full: boolean,
  singleMixed: boolean,
): OrderPackage[] | null {
  const at = '2026-08-13T07:12:00+02:00';

  if (singleMixed) return [{ number: 1, temperature: 'CHILLED', scannedAt: at }];
  if (!full) return null;

  const scanned = upper.includes('HOTOVO')
    ? [1, 2, 3]
    : upper.includes('CHYBI2')
      ? [2]
      : [1, 2];

  return [
    { number: 1, temperature: 'CHILLED', scannedAt: scanned.includes(1) ? at : null },
    { number: 2, temperature: 'FROZEN', scannedAt: scanned.includes(2) ? at : null },
    { number: 3, temperature: 'FROZEN', scannedAt: scanned.includes(3) ? at : null },
  ];
}

/**
 * Mock přehledu tras (`?mock=true&role=MASTER`).
 *
 * Tři trasy schválně pokrývají všechny tři stavy, které mistra zajímají:
 * hotovou trasu, trasu s nenaloženými balíky a trasu s nedoskenovanými.
 */
export async function mockRoutes(): Promise<RouteSummary[]> {
  await new Promise((r) => setTimeout(r, 200));

  return [
    { routeNo: '118', driverName: 'J. Kalian', vehicle: 'L12 · Renault Master', plate: '1ARI610',
      shipments: 3, parcels: 6, loadedParcels: 2, scannedParcels: 5, remainingParcels: 4, unseenParcels: 1, mine: true, complete: false },
    { routeNo: '204', driverName: 'M. Horák', vehicle: 'L07 · Fiat Ducato', plate: '2AB3456',
      shipments: 2, parcels: 3, loadedParcels: 3, scannedParcels: 3, remainingParcels: 0, unseenParcels: 0, mine: false, complete: true },
    { routeNo: '311', driverName: null, vehicle: null, plate: null,
      shipments: 1, parcels: 2, loadedParcels: 0, scannedParcels: 2, remainingParcels: 2, unseenParcels: 0, mine: false, complete: false },
  ];
}

export async function mockRouteDetail(routeNo: string): Promise<RouteDetail> {
  await new Promise((r) => setTimeout(r, 200));

  const at = '2026-08-13T07:12:00+02:00';
  const dto: RouteDetailResponseDto = {
    routeNo,
    driverName: 'J. Kalian',
    vehicle: 'L12 · Renault Master',
    plate: '1ARI610',
    shipments: [
      {
        shipmentId: 'mock-1', shipmentNumber: 'CB 2608 1300 4612', seq: 1,
        name: 'Lucie Dudáková', addr: 'Italská 2315, Kladno', sender: 'Farma Bezdínek', kg: 6,
        preloadedParcels: 2, whScannedParcels: 2,
        parcels: [
          { packageNumber: '1', temp: 'CHILLED', whScannedAt: at, loadedAt: at },
          { packageNumber: '2', temp: 'FROZEN', whScannedAt: at, loadedAt: at },
        ],
      },
      {
        shipmentId: 'mock-2', shipmentNumber: 'CB 2608 1300 4988', seq: 2,
        name: 'Ján Kováč', addr: 'Hlavná 12, Trnava', sender: 'Coolbalík CZ', kg: 14.5,
        preloadedParcels: 0, whScannedParcels: 2,
        parcels: [
          { packageNumber: '1', temp: 'AMBIENT', whScannedAt: at, loadedAt: null },
          { packageNumber: '2', temp: 'AMBIENT', whScannedAt: at, loadedAt: null },
        ],
      },
      {
        shipmentId: 'mock-3', shipmentNumber: 'CB 2608 1300 5120', seq: 3,
        name: 'Petr Malý', addr: 'Nádražní 8, Beroun', sender: null, kg: 3.2,
        preloadedParcels: 0, whScannedParcels: 1,
        parcels: [
          { packageNumber: '1', temp: 'FROZEN', whScannedAt: at, loadedAt: null },
          { packageNumber: '2', temp: 'FROZEN', whScannedAt: null, loadedAt: null },
        ],
      },
    ],
  };

  return mapRouteDetail(dto);
}

/**
 * Rozskenované zásilky pro řádek pod skenerem.
 *
 * Schválně jsou v datech i dvě zásilky, které se v seznamu objevit NESMÍ —
 * kompletně naskenovaná a vůbec nezačatá. Kdyby se filtr v `mapOverview`
 * rozbil, projeví se to hned v mocku, ne až v hale.
 */
export async function mockOverview(): Promise<OverviewShipment[]> {
  await new Promise((r) => setTimeout(r, 200));

  const at = '2026-08-16T07:12:00+02:00';
  const dto: OverviewResponseDto = {
    date: '2026-08-16',
    shipments: [
      {
        shipmentId: 'mock-ov-1', shipmentNumber: 'CB 2608 1300 5120', seq: 3, routeNo: '118',
        name: 'Petr Malý', addr: 'Nádražní 8, Beroun', sender: null, kg: 3.2,
        preloadedParcels: 0, whScannedParcels: 1,
        parcels: [
          { packageNumber: '1', temp: 'FROZEN', whScannedAt: at, loadedAt: null },
          { packageNumber: '2', temp: 'FROZEN', whScannedAt: null, loadedAt: null },
          { packageNumber: '3', temp: 'FROZEN', whScannedAt: null, loadedAt: null },
        ],
      },
      {
        shipmentId: 'mock-ov-2', shipmentNumber: 'CB 2608 1300 4988', seq: 2, routeNo: null,
        name: 'Ján Kováč', addr: 'Hlavná 12, Trnava', sender: 'Coolbalík CZ', kg: 14.5,
        preloadedParcels: 0, whScannedParcels: 1,
        parcels: [
          { packageNumber: '1', temp: 'AMBIENT', whScannedAt: at, loadedAt: null },
          { packageNumber: '2', temp: 'AMBIENT', whScannedAt: null, loadedAt: null },
        ],
      },
      {
        shipmentId: 'mock-ov-3', shipmentNumber: 'CB 2608 1300 4612', seq: 1, routeNo: '118',
        name: 'Lucie Dudáková', addr: 'Italská 2315, Kladno', sender: null, kg: 6,
        preloadedParcels: 0, whScannedParcels: 2,
        parcels: [
          { packageNumber: '1', temp: 'CHILLED', whScannedAt: at, loadedAt: null },
          { packageNumber: '2', temp: 'CHILLED', whScannedAt: at, loadedAt: null },
        ],
      },
      {
        shipmentId: 'mock-ov-4', shipmentNumber: 'CB 2608 1300 7001', seq: 4, routeNo: '204',
        name: 'Eva Nová', addr: 'Krátká 3, Slaný', sender: null, kg: 1.1,
        preloadedParcels: 0, whScannedParcels: 0,
        parcels: [
          { packageNumber: '1', temp: 'AMBIENT', whScannedAt: null, loadedAt: null },
          { packageNumber: '2', temp: 'AMBIENT', whScannedAt: null, loadedAt: null },
        ],
      },
    ],
  };

  return mapOverview(dto);
}
